// apps/web/src/adapters/DexieDB.ts
// ─────────────────────────────────────────────────────────────────────────
// IndexedDB schema for Nisk. Uses Dexie for ergonomic queries.
// Bump VERSION + add a `db.version(N).stores(...)` block when schema changes.
// ─────────────────────────────────────────────────────────────────────────

import Dexie, { type Table } from 'dexie';
import { STORAGE_TABLES } from '@nisk/core';

interface KVRow {
	key: string;
	value: unknown;
}

interface BlobRow {
	path: string;
	blob: Blob;
	mimeType: string;
	sizeBytes: number;
	createdAt: number;
}

export class NiskDB extends Dexie {
	// KV tables (one per logical area, all sharing the same shape)
	[STORAGE_TABLES.LESSONS]!: Table<KVRow, string>;
	[STORAGE_TABLES.MODULES]!: Table<KVRow, string>;
	[STORAGE_TABLES.PROGRESS]!: Table<KVRow, string>;
	[STORAGE_TABLES.SRS_RECORDS]!: Table<KVRow, string>;
	[STORAGE_TABLES.SETTINGS]!: Table<KVRow, string>;
	[STORAGE_TABLES.SYNC_QUEUE]!: Table<KVRow, string>;
	[STORAGE_TABLES.OPERATIONS]!: Table<KVRow, string>;
	[STORAGE_TABLES.ECONOMY]!: Table<KVRow, string>;
	[STORAGE_TABLES.DOWNLOADS]!: Table<KVRow, string>;
	[STORAGE_TABLES.DOWNLOAD_META]!: Table<KVRow, string>;

	// Blob storage for audio files
	audioBlobs!: Table<BlobRow, string>;

	constructor() {
		super('NiskDB');

		// Version 1 — initial schema
		this.version(1).stores({
			[STORAGE_TABLES.LESSONS]: 'key',
			[STORAGE_TABLES.MODULES]: 'key',
			[STORAGE_TABLES.PROGRESS]: 'key',
			[STORAGE_TABLES.SRS_RECORDS]: 'key',
			[STORAGE_TABLES.SETTINGS]: 'key',
			[STORAGE_TABLES.SYNC_QUEUE]: 'key',
			[STORAGE_TABLES.OPERATIONS]: 'key',
			[STORAGE_TABLES.ECONOMY]: 'key',
			[STORAGE_TABLES.DOWNLOADS]: 'key',
			[STORAGE_TABLES.DOWNLOAD_META]: 'key',
			audioBlobs: 'path, createdAt',
		});
	}
}

// Singleton instance — Dexie handles connection pooling internally
export const db = new NiskDB();
